import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PLAN_TYPE } from '@/const/plans';
import { formatPrice, showError } from '@/utils';
import { fetchPrices } from '@/store/checkout/thunks';
import { TRIAL_DAYS } from '@/config';

const fetchPricesPromises = {};

export function usePlanOptions(prices, { withPeriod = false } = {}) {
	const { t } = useTranslation();

	if (!prices?.length) {
		return [];
	}

	return prices.map(plan => {
		return {
			id: plan.handlerId,
			name: plan.name,
			label: t(`plans.${plan.name}.name`, { trialDays: TRIAL_DAYS }),
			description: t(`plans.${plan.name}.description`, {
				trialDays: TRIAL_DAYS
			}),
			price: formatPrice({
				amount: plan.baseAmount,
				currency: plan.currency
			}),
			rightLabelSuffix:
				withPeriod && plan.name !== PLAN_TYPE.TRIAL
					? t('common.monthly')
					: ''
		};
	});
}

export function usePlans() {
	const [loading, setLoading] = useState(false);
	const [pricesData, setPricesData] = useState([]);
	const currency = useSelector(state => state.auth.currency);
	const dispatch = useDispatch();

	const country = useSelector(
		state =>
			state.auth?.user?.taxInformation?.country || state.auth?.user?.country
	);

	let servicePlan = null;
	let storagePlan = null;
	let trialPlan = null;
	let monthlyPlan = null;
	let mainPlan = null;

	for (const currentItem of pricesData) {
		if (servicePlan && trialPlan) {
			break;
		}

		if (currentItem.main && currentItem.active) {
			mainPlan = currentItem;
		}

		if (currentItem.name === PLAN_TYPE.TRIAL && currentItem.active) {
			trialPlan = currentItem;
		}

		if (currentItem.name === PLAN_TYPE.MONTHLY && currentItem.active) {
			monthlyPlan = currentItem;
		}

		if (currentItem.name === PLAN_TYPE.SERVICE && currentItem.active) {
			servicePlan = currentItem;
		}

		if (currentItem.name === PLAN_TYPE.STORAGE && currentItem.active) {
			storagePlan = currentItem;
		}
	}

	const plans = useMemo(
		() =>
			pricesData
				?.filter(
					plan =>
						plan.name !== PLAN_TYPE.TRIAL &&
						plan.name !== PLAN_TYPE.SERVICE &&
						plan.name !== PLAN_TYPE.STORAGE
				)
				.sort((plan1, plan2) => plan1.baseAmount - plan2.baseAmount),
		[pricesData]
	);

	const planOptions = usePlanOptions(plans);

	useEffect(() => {
		(async () => {
			try {
				setLoading(true);

				let promise = fetchPricesPromises[currency];

				if (!promise) {
					promise = dispatch(
						fetchPrices({ country, currency, filterService: false })
					).unwrap();
					fetchPricesPromises[currency] = promise;
				}

				const response = await promise;

				setPricesData(response.prices);
			} catch (error) {
				showError(error);
			} finally {
				setLoading(false);
				fetchPricesPromises[currency] = null;
			}
		})();
	}, [currency]);

	return {
		trialPlan,
		servicePlan,
		storagePlan,
		plans,
		loading,
		prices: pricesData,
		planOptions,
		monthlyPlan,
		mainPlan
	};
}
